import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout from "@/components/app/Layout";
import Login from "@/pages/Login";
import PublicLanding from "@/pages/PublicLanding";
import IndustriesAdmin from "@/pages/IndustriesAdmin";
import Reactivation from "@/pages/Reactivation";
import ReactivationIntake from "@/pages/ReactivationIntake";
import ReactivationIntakes from "@/pages/ReactivationIntakes";
import ReactivationNeedsHuman from "@/pages/ReactivationNeedsHuman";
import ReactivationPortal from "@/pages/ReactivationPortal";
import ReactivationDigests from "@/pages/ReactivationDigests";
import WebhookSettings from "@/pages/WebhookSettings";
import ResendConfigAdmin from "@/pages/ResendConfigAdmin";
import TwilioConfigAdmin from "@/pages/TwilioConfigAdmin";
import ApolloConfigAdmin from "@/pages/ApolloConfigAdmin";
import ApolloSearch from "@/pages/ApolloSearch";
import ApiTokensAdmin from "@/pages/ApiTokensAdmin";
import AuditConsole from "@/pages/AuditConsole";
import AuditReport from "@/pages/AuditReport";
import AuditConsoleConfigAdmin from "@/pages/AuditConsoleConfigAdmin";
import BookACall from "@/pages/BookACall";
import Dashboard from "@/pages/Dashboard";
import LeadDiscovery from "@/pages/LeadDiscovery";
import LeadsBoard from "@/pages/LeadsBoard";
import LeadDetail from "@/pages/LeadDetail";
import PublicLeads from "@/pages/PublicLeads";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import ClientCredentials from "@/pages/ClientCredentials";
import ClientOnboarding from "@/pages/ClientOnboarding";
import PublicAiSeoAudit from "@/pages/PublicAiSeoAudit";
import AiSeoReport from "@/pages/AiSeoReport";
import Onboarding from "@/pages/Onboarding";
import Marketing from "@/pages/Marketing";
import Industries from "@/pages/Industries";
import IndustryLanding from "@/pages/IndustryLanding";
import Directory from "@/pages/Directory";
import Pricing from "@/pages/Pricing";
import OnboardingPublic from "@/pages/OnboardingPublic";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";
import AgentsList from "@/pages/AgentsList";
import AgentEditor from "@/pages/AgentEditor";
import AgentDemo from "@/pages/AgentDemo";
import AgentPublicDemo from "@/pages/AgentPublicDemo";
import WidgetConversations from "@/pages/WidgetConversations";
import ROISignals from "@/pages/ROISignals";
import Sequences from "@/pages/Sequences";
import Reports from "@/pages/Reports";
import Domains, { DomainDetail, DomainIntakePublic } from "@/pages/Domains";
import Settings from "@/pages/Settings";
import Subscribers from "@/pages/Subscribers";
import Bookings from "@/pages/Bookings";
import Conversations from "@/pages/Conversations";
import Help from "@/pages/Help";
import Roleplay from "@/pages/Roleplay";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import ReactivationMetrics from "@/pages/ReactivationMetrics";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutCancel from "@/pages/CheckoutCancel";
import AddOns from "@/pages/AddOns";
import Outreach from "@/pages/Outreach";
import CalendarPage from "@/pages/CalendarPage";
import Watchlists from "@/pages/Watchlists";
import ClientPortal from "@/pages/ClientPortal";
import PortalAnalytics from "@/pages/PortalAnalytics";
import Users from "@/pages/Users";
import ResetPassword from "@/pages/ResetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import Intakes from "@/pages/Intakes";
import IntakeAIVisibility from "@/pages/IntakeAIVisibility";
import AIVisibilityAuditView from "@/pages/AIVisibilityAuditView";
import AIVisibilityHub from "@/pages/AIVisibilityHub";
import Files from "@/pages/Files";
import Contracts from "@/pages/Contracts";
import Contractors from "@/pages/Contractors";
import ServicePackages from "@/pages/ServicePackages";
import PublicSign from "@/pages/PublicSign";
import PublicMilestones from "@/pages/PublicMilestones";
import Jobs from "@/pages/Jobs";
import Payouts from "@/pages/Payouts";
import Security from "@/pages/Security";
import PublicAudit from "@/pages/PublicAudit";
import PublicAIVisibility from "@/pages/PublicAIVisibility";
import PublicWinWall from "@/pages/PublicWinWall";
import PublicFreeAudit from "@/pages/PublicFreeAudit";
import PricingPackages from "@/pages/PricingPackages";
import GetStarted from "@/pages/GetStarted";
import AiVisibilityQuiz from "@/pages/AiVisibilityQuiz";
import Benchmarks from "@/pages/Benchmarks";
import MissedCallConfig from "@/pages/MissedCallConfig";
import ReviewTemplates from "@/pages/ReviewTemplates";
import VapiConcierge from "@/components/VapiConcierge";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-neutral-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  // Show concierge on public marketing pages only (not admin, login, preview, etc.)
  const publicRoutes = ["/", "/industries", "/for", "/directory", "/pricing-public",
                        "/packages", "/get-started", "/ai-visibility-quiz", "/onboarding-public", "/privacy", "/terms"];
  const showConcierge = publicRoutes.some((r) => path === r || (r !== "/" && path.startsWith(r))) || path === "/";
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public marketing site */}
            <Route path="/" element={<PublicLanding />} />
            <Route path="/free-ai-seo-audit" element={<PublicAiSeoAudit />} />
            <Route path="/ai-seo-audit-report/:id" element={<AiSeoReport />} />
            <Route path="/book" element={<BookACall />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/for/:industry" element={<IndustryLanding />} />
            <Route path="/directory" element={<Directory />} />
            <Route path="/pricing-public" element={<Pricing />} />
            <Route path="/onboarding-public" element={<OnboardingPublic />} />
            <Route path="/demo/agent/:id" element={<AgentPublicDemo />} />
            <Route path="/domain-intake/:id" element={<DomainIntakePublic />} />
            <Route path="/login" element={<Login />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/portal/:token" element={<ClientPortal />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/intake/ai-visibility/:token" element={<IntakeAIVisibility />} />
            <Route path="/audit" element={<PublicAudit />} />
            <Route path="/ai-visibility" element={<PublicAIVisibility />} />
            <Route path="/wins" element={<PublicWinWall />} />
            <Route path="/free-audit" element={<PublicFreeAudit />} />
            <Route path="/packages" element={<PricingPackages />} />
            <Route path="/get-started" element={<GetStarted />} />
            <Route path="/ai-visibility-quiz" element={<AiVisibilityQuiz />} />
            <Route path="/benchmarks" element={<Benchmarks />} />
            <Route path="/reactivation-metrics" element={<ReactivationMetrics />} />
            <Route path="/audit/public/:id/:token" element={<AuditReport />} />
            <Route path="/sign/:token" element={<PublicSign />} />
            <Route path="/status/:token" element={<PublicMilestones />} />
            <Route path="/reactivation-intake/:token" element={<ReactivationIntake />} />
            <Route path="/reactivation-portal/:token" element={<ReactivationPortal />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/cancel" element={<CheckoutCancel />} />

            {/* Theme previews — public, non-destructive */}
            {/* /preview routes intentionally removed — these were internal
                designer prototypes ("THEME PREVIEW · live site untouched") that
                have no place on the public site. Files kept in src/pages/ for
                design reference only. */}

            {/* Admin console */}
            <Route
              element={
                <Protected>
                  <Layout />
                </Protected>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/industries-admin" element={<IndustriesAdmin />} />
              <Route path="/reactivation" element={<Reactivation />} />
              <Route path="/reactivation/intakes" element={<ReactivationIntakes />} />
              <Route path="/reactivation/needs-human" element={<ReactivationNeedsHuman />} />
              <Route path="/reactivation/digests" element={<ReactivationDigests />} />
              <Route path="/settings/webhook" element={<WebhookSettings />} />
              <Route path="/settings/resend" element={<ResendConfigAdmin />} />
              <Route path="/settings/twilio" element={<TwilioConfigAdmin />} />
              <Route path="/settings/apollo" element={<ApolloConfigAdmin />} />
              <Route path="/settings/api-tokens" element={<ApiTokensAdmin />} />
              <Route path="/settings/audit-console" element={<AuditConsoleConfigAdmin />} />
              <Route path="/audit-console" element={<AuditConsole />} />
              <Route path="/audit-console/report/:id" element={<AuditReport />} />
              <Route path="/settings/missed-call" element={<MissedCallConfig />} />
              <Route path="/review-templates" element={<ReviewTemplates />} />
              <Route path="/discover" element={<LeadDiscovery />} />
              <Route path="/discover/apollo" element={<ApolloSearch />} />
              <Route path="/leads" element={<LeadsBoard />} />
              <Route path="/leads/:id" element={<LeadDetail />} />
              <Route path="/inbound" element={<PublicLeads />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/clients/:id/credentials" element={<ClientCredentials />} />
              <Route path="/clients/:id/onboarding" element={<ClientOnboarding />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/marketing" element={<Marketing />} />
              <Route path="/addons" element={<AddOns />} />
              <Route path="/outreach" element={<Outreach />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/watchlists" element={<Watchlists />} />
              <Route path="/portal-analytics" element={<PortalAnalytics />} />
              <Route path="/agents" element={<AgentsList />} />
              <Route path="/agents/new" element={<AgentEditor />} />
              <Route path="/agents/:id" element={<AgentEditor />} />
              <Route path="/agents/:id/demo" element={<AgentDemo />} />
              <Route path="/widget-chats" element={<WidgetConversations />} />
              <Route path="/roi-signals" element={<ROISignals />} />
              <Route path="/sequences" element={<Sequences />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/domains" element={<Domains />} />
              <Route path="/domains/:id" element={<DomainDetail />} />
              <Route path="/bookings" element={<Bookings />} />
              <Route path="/conversations" element={<Conversations />} />
              <Route path="/subscribers" element={<Subscribers />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/billing/cancel" element={<BillingCancel />} />
              <Route path="/users" element={<Users />} />
              <Route path="/intakes" element={<Intakes />} />
              <Route path="/ai-visibility-hub" element={<AIVisibilityHub />} />
              <Route path="/files" element={<Files />} />
              <Route path="/contracts" element={<Contracts />} />
              <Route path="/contractors" element={<Contractors />} />
              <Route path="/service-packages" element={<ServicePackages />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/payouts" element={<Payouts />} />
              <Route path="/security" element={<Security />} />
              <Route path="/reports/ai-visibility" element={<AIVisibilityAuditView />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/help" element={<Help />} />
              <Route path="/help/roleplay" element={<Roleplay />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
        {showConcierge && <VapiConcierge />}
      </BrowserRouter>
    </div>
  );
}
